import React from "react";
import { Document, Page, Text, View, StyleSheet, Font } from "@react-pdf/renderer";
import manual from "../data/reappro_manual.json";

/**
 * Phase 101gd (Feb 2026) — Manuel opératoire tablette réappro
 * Version FR + TH côte à côte pour les livreurs bilingues Maison Thaï.
 *
 * Contenu traduit via Claude Sonnet 4.6 (Emergent LLM Key).
 * Termes techniques (Nayax, DLC, OF, noms clients) conservés en français.
 *
 * Police thaï : Noto Sans Thai (Google Fonts CDN, libre).
 */

// Enregistre la police Noto Sans Thai (bundlée localement dans public/fonts/
// pour ne pas dépendre du CDN Google Fonts qui change ses URLs — la v25
// utilisée en premier a renvoyé 404 après quelques mois).
Font.register({
  family: "NotoThai",
  fonts: [
    { src: "/fonts/NotoSansThai-Regular.ttf", fontWeight: 400 },
    { src: "/fonts/NotoSansThai-Bold.ttf", fontWeight: 700 },
  ],
});

const COLOR = {
  primary: "#1F4E3D",
  accent: "#8C6B2F",
  danger: "#C05C47",
  ok: "#5B7A5F",
  text: "#1A1C1A",
  muted: "#575A57",
  bgAlt: "#F5F9F6",
  border: "#C5DBCF",
};

const styles = StyleSheet.create({
  page: {
    paddingTop: 24,
    paddingHorizontal: 28,
    paddingBottom: 40,
    fontFamily: "Helvetica",
    fontSize: 9,
    color: COLOR.text,
  },
  header: {
    borderBottom: `2 solid ${COLOR.primary}`,
    paddingBottom: 8,
    marginBottom: 12,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
  },
  brand: { fontSize: 10, color: COLOR.primary, fontFamily: "Helvetica-Bold" },
  titleFr: { fontSize: 15, fontFamily: "Helvetica-Bold", color: COLOR.text, marginTop: 2 },
  titleTh: { fontSize: 13, fontFamily: "NotoThai", fontWeight: 700, color: COLOR.text, marginTop: 1 },
  date: { fontSize: 8, color: COLOR.muted },

  intro: { marginBottom: 10, padding: 8, backgroundColor: COLOR.bgAlt, borderLeft: `3 solid ${COLOR.primary}`, borderRadius: 2 },
  introRow: { flexDirection: "row", gap: 10, marginBottom: 3 },
  introFr: { flex: 1, fontSize: 9, color: COLOR.text, lineHeight: 1.35 },
  introTh: { flex: 1, fontFamily: "NotoThai", fontSize: 9, color: COLOR.text, lineHeight: 1.4 },

  partTitle: {
    fontSize: 12, fontFamily: "Helvetica-Bold", color: "#fff",
    backgroundColor: COLOR.primary, padding: 5, marginTop: 8, marginBottom: 6, borderRadius: 2,
  },
  partTitleTh: { fontFamily: "NotoThai", fontSize: 10, color: "#fff", marginTop: 1 },

  section: { marginBottom: 6, breakInside: "avoid" },
  sectionTitleRow: { flexDirection: "row", gap: 10, alignItems: "baseline", marginBottom: 2, paddingBottom: 1, borderBottom: `1 dotted ${COLOR.border}` },
  sectionTitleFr: { flex: 1, fontSize: 10, fontFamily: "Helvetica-Bold", color: COLOR.primary },
  sectionTitleTh: { flex: 1, fontFamily: "NotoThai", fontSize: 10, fontWeight: 700, color: COLOR.primary },

  bodyRow: { flexDirection: "row", gap: 10, marginBottom: 3 },
  bodyFr: { flex: 1, fontSize: 8.5, color: COLOR.text, lineHeight: 1.4 },
  bodyTh: { flex: 1, fontFamily: "NotoThai", fontSize: 8.5, color: COLOR.text, lineHeight: 1.45 },

  rule: {
    flexDirection: "row", gap: 10, marginBottom: 2,
    backgroundColor: "#FBF4E9", padding: 4, borderRadius: 2,
    borderLeft: `2 solid ${COLOR.accent}`,
  },
  ruleFr: { flex: 1, fontSize: 8.5, color: COLOR.text },
  ruleTh: { flex: 1, fontFamily: "NotoThai", fontSize: 8.5, color: COLOR.text, lineHeight: 1.4 },

  footerBar: {
    position: "absolute", bottom: 15, left: 28, right: 28,
    borderTop: `1 solid ${COLOR.border}`, paddingTop: 6,
    flexDirection: "row", justifyContent: "space-between",
    fontSize: 7, color: COLOR.muted,
  },
});

function Section({ fr, th, titleFr, titleTh }) {
  return (
    <View style={styles.section} wrap={false}>
      <View style={styles.sectionTitleRow}>
        <Text style={styles.sectionTitleFr}>{titleFr}</Text>
        <Text style={styles.sectionTitleTh}>{titleTh}</Text>
      </View>
      <View style={styles.bodyRow}>
        <Text style={styles.bodyFr}>{fr}</Text>
        <Text style={styles.bodyTh}>{th}</Text>
      </View>
    </View>
  );
}

function Rule({ fr, th }) {
  return (
    <View style={styles.rule} wrap={false}>
      <Text style={styles.ruleFr}>{fr}</Text>
      <Text style={styles.ruleTh}>{th}</Text>
    </View>
  );
}

export default function ReapproManualPdf() {
  const { fr, th } = manual;
  const today = new Date().toLocaleDateString("fr-CH", { day: "2-digit", month: "long", year: "numeric" });
  return (
    <Document title="Procédure tablette réappro — Maison Thaï" author="Maison Thaï">
      <Page size="A4" style={styles.page}>
        <View style={styles.header} fixed>
          <View>
            <Text style={styles.brand}>MAISON THAÏ · AgroManage</Text>
            <Text style={styles.titleFr}>{fr.title}</Text>
            <Text style={styles.titleTh}>{th.title}</Text>
          </View>
          <Text style={styles.date}>{today}</Text>
        </View>

        <View style={styles.intro} wrap={false}>
          <View style={styles.introRow}>
            <Text style={styles.introFr}>{fr.intro}</Text>
            <Text style={styles.introTh}>{th.intro}</Text>
          </View>
        </View>

        {/* Partie A */}
        <View wrap={false}>
          <Text style={styles.partTitle}>{fr.part_a_title}</Text>
          <Text style={styles.partTitleTh}>{th.part_a_title}</Text>
        </View>
        <Section titleFr={fr.a1_title} titleTh={th.a1_title} fr={fr.a1_body} th={th.a1_body} />
        <Section titleFr={fr.a2_title} titleTh={th.a2_title} fr={fr.a2_body} th={th.a2_body} />
        <Section titleFr={fr.a3_title} titleTh={th.a3_title} fr={fr.a3_body} th={th.a3_body} />
        <Section titleFr={fr.a4_title} titleTh={th.a4_title} fr={fr.a4_body} th={th.a4_body} />
        <Rule fr={fr.a4_rule1} th={th.a4_rule1} />
        <Rule fr={fr.a4_rule2} th={th.a4_rule2} />
        <Rule fr={fr.a4_rule3} th={th.a4_rule3} />
        <Rule fr={fr.a4_rule4} th={th.a4_rule4} />
        <View style={styles.bodyRow}>
          <Text style={styles.bodyFr}>{fr.a4_scan}</Text>
          <Text style={styles.bodyTh}>{th.a4_scan}</Text>
        </View>
        <Section titleFr={fr.a5_title} titleTh={th.a5_title} fr={fr.a5_body} th={th.a5_body} />

        {/* Partie B */}
        <View wrap={false}>
          <Text style={styles.partTitle}>{fr.part_b_title}</Text>
          <Text style={styles.partTitleTh}>{th.part_b_title}</Text>
        </View>
        <Section titleFr={fr.b1_title} titleTh={th.b1_title} fr={fr.b1_body} th={th.b1_body} />
        <Section titleFr={fr.b2_title} titleTh={th.b2_title} fr={fr.b2_body} th={th.b2_body} />
        <Section titleFr={fr.b3_title} titleTh={th.b3_title} fr={fr.b3_body} th={th.b3_body} />
        <Section titleFr={fr.b4_title} titleTh={th.b4_title} fr={fr.b4_body} th={th.b4_body} />
        <Section titleFr={fr.b5_title} titleTh={th.b5_title} fr={fr.b5_body} th={th.b5_body} />
        <Section titleFr={fr.b6_title} titleTh={th.b6_title} fr={fr.b6_body} th={th.b6_body} />

        {/* Fin */}
        <Section titleFr={fr.end_title} titleTh={th.end_title} fr={fr.end_body} th={th.end_body} />
        <Section titleFr={fr.help_title} titleTh={th.help_title} fr={fr.help_body} th={th.help_body} />

        <View style={styles.footerBar} fixed>
          <Text>Maison Thaï SA · Conthey (VS) · info@maison-thai.pro</Text>
          <Text render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
        </View>
      </Page>
    </Document>
  );
}
